import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  Divider,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeactivateAccountDialog from "./DeactivateAccountDialog";
import DeleteAccountDialog from "./DeleteAccountDialog";

interface AccountManagementSectionProps {
  onExportData: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
}

const AccountManagementSection: React.FC<AccountManagementSectionProps> = ({
  onExportData,
  onDeactivate,
  onDelete,
}) => {
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportSnackbar, setExportSnackbar] = useState(false);

  const handleExport = () => {
    onExportData();
    setExportSnackbar(true);
  };

  const handleDeactivate = () => {
    onDeactivate();
    setDeactivateOpen(false);
  };

  const handleDelete = () => {
    onDelete();
    setDeleteOpen(false);
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Account Management
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage your account data and status
      </Typography>

      <Stack divider={<Divider />} spacing={0}>
        {/* Export Data */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            py: 2,
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <DownloadIcon sx={{ color: "#178B8E", fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>
                Export Your Data
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Download a copy of all your data including bookings, messages, and profile
              information.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              We'll email you a download link within 24 hours.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{
              textTransform: "none",
              borderColor: "#178B8E",
              color: "#178B8E",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Request Export
          </Button>
        </Box>

        {/* Deactivate Account */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            py: 2,
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <PauseCircleOutlineIcon sx={{ color: "warning.main", fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>
                Deactivate Account
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Temporarily disable your account. You can reactivate anytime by logging back in.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={() => setDeactivateOpen(true)}
            sx={{ textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Deactivate
          </Button>
        </Box>

        {/* Delete Account */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            py: 2,
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <DeleteForeverIcon sx={{ color: "error.main", fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600} color="error.main">
                Delete Account
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Permanently delete your account and all associated data. This cannot be undone.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => setDeleteOpen(true)}
            sx={{ textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Delete Account
          </Button>
        </Box>
      </Stack>

      <DeactivateAccountDialog
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onDeactivate={handleDeactivate}
      />
      <DeleteAccountDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDelete={handleDelete}
      />

      <Snackbar
        open={exportSnackbar}
        autoHideDuration={4000}
        onClose={() => setExportSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setExportSnackbar(false)}>
          Export requested! You'll receive an email with a download link within 24 hours.
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AccountManagementSection;
